import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faUsers } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import useTeam, { generateUrls } from '@hooks/useTeam'
import useTeams from '@hooks/useTeams'

import type { TeamFilters } from '@hooks/useTeams'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import defaultRequestOptions from '@sections/Client/packs/AgencyAdmin/defaultRequestOptions'

import TeamListItem from '../TeamListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        entityKey: 'users',
        entityTitleKey: 'full_name',
        style,
      },
      key: 'user_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: TeamFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const { selectedAgencyId } = useAgencySelector()

  const teamsPayload = useTeams({
    filters: {
      ...pageFilters,
      owner_id: selectedAgencyId,
      owner_type: 'Agency',
    },
    performHttpRequests: true,
    requestOptions: {
      ...defaultRequestOptions.team,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredTeams,
    filteredTeamsCount,
    hasFilteredTeams,
    loading,
  } = teamsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const initTeam = { owner_id: selectedAgencyId, owner_type: 'Agency' }

  const { teamsIndexUrl } = generateUrls({}, { prefixRoute: `agencies/${selectedAgencyId}/manage` })

  const {
    callbacks: { addTeam },
  } = useTeam(initTeam)

  return (
    <>
      <Helmet>
        <title>Teams | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => addTeam()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="teams"
        nestedNavigation
        tabBarItems={[
          {
            href: teamsIndexUrl,
            icon: faUsers,
            key: 'teams',
            title: 'Teams',
          },
        ]}
        title="Teams"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.teams.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTeams && <ListBlankState />}

            {!loading && hasFilteredTeams && (
              <DashboardModule title="Teams">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredTeams.map(team => (
                      <TeamListItem key={team.id} team={team} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredTeamsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.teams.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
